/**
 * @typedef {import("posthog-js").CaptureResult} CaptureResult
 */

const BENIGN_MESSAGES = [
  // Cross-origin script errors are masked to this with no stack
  /^Script error\.?$/,
  // Stackless spec notice: the remaining notifications are delivered next frame
  /^ResizeObserver loop completed with undelivered notifications\.?$/,
];

const APP_SCRIPT_URL = /^https?:\/\//;

/**
 * True for benign browser messages, or stacks with no http(s) frame (extensions, injected code).
 * @param {Array<{ value?: string, stacktrace?: { frames?: Array<{ filename?: string }> } }>} exceptionList
 * @returns {boolean}
 */
const isUnactionableException = (exceptionList) => {
  if (!exceptionList?.length) return false;

  const frames = exceptionList.flatMap((exception) => exception.stacktrace?.frames ?? []);

  if (frames.length) {
    return !frames.some((frame) => APP_SCRIPT_URL.test(frame.filename ?? ""));
  }

  return exceptionList.every((exception) =>
    BENIGN_MESSAGES.some((pattern) => pattern.test(exception.value ?? "")),
  );
};

/**
 * posthog-js `before_send` hook that drops unactionable `$exception` events.
 * @param {CaptureResult | null} event
 * @returns {CaptureResult | null}
 */
export const dropUnactionableExceptions = (event) =>
  event?.event === "$exception" && isUnactionableException(event.properties?.$exception_list)
    ? null
    : event;

// Errors thrown from mousemove, rAF or ResizeObserver callbacks re-fire every frame
const REPEAT_WINDOW_MS = 1000;
const MAX_TRACKED_FINGERPRINTS = 200;

/**
 * Identifies an exception by type, message and throw site.
 * @param {Array<{ type?: string, value?: string, stacktrace?: { frames?: Array<{ filename?: string, lineno?: number, colno?: number }> } }>} exceptionList
 * @returns {string}
 */
const fingerprint = (exceptionList) =>
  JSON.stringify(
    exceptionList.map(({ type, value, stacktrace }) => {
      const frame = stacktrace?.frames?.at(-1);
      return [type, value, frame?.filename, frame?.lineno, frame?.colno];
    }),
  );

/**
 * Builds a posthog-js `before_send` hook that sends an `$exception` at most once
 * per REPEAT_WINDOW_MS, so an error re-thrown every frame isn't reported dozens of times.
 * @param {() => number} [now]
 * @returns {(event: CaptureResult | null) => CaptureResult | null}
 */
export const createRepeatedExceptionFilter = (now = Date.now) => {
  const lastSentAt = new Map();

  return (event) => {
    const exceptionList = event?.event === "$exception" && event.properties?.$exception_list;
    if (!exceptionList?.length) return event;

    const key = fingerprint(exceptionList);
    const time = now();
    if (time - (lastSentAt.get(key) ?? -Infinity) < REPEAT_WINDOW_MS) return null;

    if (lastSentAt.size >= MAX_TRACKED_FINGERPRINTS) lastSentAt.clear();
    lastSentAt.set(key, time);
    return event;
  };
};
